import React from 'react';
import {Pressable, StyleSheet, View, type StyleProp, type ViewStyle} from 'react-native';

import {Divider, IconBubble} from '@/shared/ui/card';
import {Icon, type IconName} from '@/shared/ui/icons';
import {AppText} from '@/shared/ui/text';
import {Pager} from '@/shared/ui/pager';
import {useClientPage} from '@/shared/lib/use-client-page';
import {radii, spacing, useTheme} from '@/shared/ui/theme';

/**
 * The grouped list — the shape every settings screen on both platforms uses, and the
 * one the cabinet's management surfaces were missing.
 *
 * The old dashboard stacked four separately-outlined boxes with gaps between them; four
 * links that belong together were drawn as four unrelated objects. `RowGroup` puts them
 * on ONE card separated by inset hairlines, which is both quieter and a stronger claim:
 * these things are a set.
 *
 * No user-facing string is baked in — titles, subtitles and values all arrive as props.
 */
const styles = StyleSheet.create({
  paged: {gap: spacing(2)},
  /* Панель: рамка `border-default`, радиус 3, ни одной тени. Строки внутри делятся
     линиями `border-subtle`. */
  group: {borderRadius: radii.md, borderWidth: StyleSheet.hairlineWidth, overflow: 'hidden'},
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing(3),
    paddingHorizontal: spacing(3),
    paddingVertical: spacing(2) + 2,
    minHeight: 48,
  },
  body: {flex: 1, gap: 1},
  trailing: {flexDirection: 'row', alignItems: 'center', gap: spacing(2)},
});

/** Строк на странице в списке настроек, и что предлагает рейка под ним. */
const PAGED_ROWS_PER_PAGE = 20;
const PAGED_ROWS_SIZES = [20, 50, 100] as const;

export function RowGroup({
  children,
  style,
  testID,
}: {
  children: React.ReactNode;
  style?: StyleProp<ViewStyle>;
  testID?: string;
}) {
  const p = useTheme();
  // Nulls are filtered so a conditional row (`cond ? <ListRow/> : null`) does not leave
  // a divider hanging over nothing.
  const rows = React.Children.toArray(children).filter(Boolean);
  return (
    <View
      testID={testID}
      style={[styles.group, {backgroundColor: p.card, borderColor: p.border}, style]}>
      {rows.map((row, i) => (
        <React.Fragment key={i}>
          {i > 0 ? <Divider /> : null}
          {row}
        </React.Fragment>
      ))}
    </View>
  );
}

export type ListRowProps = {
  title: string;
  subtitle?: string | null;
  /**
   * Глиф на приглушённой подложке. Игнорируется, если задан `leading`.
   *
   * Раньше рядом жил `moduleId`: строка спрашивала у реестра модулей, какой значок
   * носить, и заодно получала оттуда ЦВЕТ раздела — из-за чего список настроек
   * превращался в цветной пускатель. Реестра больше нет; значок называет вызывающий,
   * а раздел опознаётся словами.
   */
  icon?: IconName;
  /** Wash behind the icon bubble; defaults to the module's, then the muted grey. */
  iconTone?: string;
  /** Colour of the glyph itself; defaults to the module's, then the foreground. */
  iconColor?: string;
  /** Anything else on the left — an avatar, a status dot. */
  leading?: React.ReactNode;
  /** A short value printed on the right, before the chevron. */
  value?: string | null;
  /** Anything else on the right — a pill, a switch. */
  trailing?: React.ReactNode;
  onPress?: () => void;
  /** Force the chevron on/off; by default it follows `onPress`. */
  chevron?: boolean;
  /** Paints the title in the destructive tone — sign out, delete. */
  danger?: boolean;
  testID?: string;
  style?: StyleProp<ViewStyle>;
};

export function ListRow({
  title,
  subtitle,
  icon,
  iconTone,
  iconColor,
  leading,
  value,
  trailing,
  onPress,
  chevron,
  danger = false,
  testID,
  style,
}: ListRowProps) {
  const p = useTheme();
  const showChevron = chevron ?? !!onPress;
  const titleColor = danger ? p.destructive : p.foreground;

  const glyph = icon;

  const content = (
    <>
      {leading ??
        (glyph ? (
          <IconBubble size={30} tone={iconTone}>
            <Icon name={glyph} size={16} color={iconColor ?? p.textSecondary} />
          </IconBubble>
        ) : null)}

      <View style={styles.body}>
        <AppText variant="subtitle" color={titleColor} numberOfLines={1}>
          {title}
        </AppText>
        {subtitle ? (
          <AppText variant="caption" numberOfLines={2}>
            {subtitle}
          </AppText>
        ) : null}
      </View>

      <View style={styles.trailing}>
        {value ? (
          <AppText variant="caption" numberOfLines={1}>
            {value}
          </AppText>
        ) : null}
        {trailing}
        {showChevron ? (
          <View
            testID="list-row-chevron"
            style={{
              width: 7,
              height: 7,
              borderRightWidth: 1.5,
              borderTopWidth: 1.5,
              borderColor: p.mutedForeground,
              transform: [{rotate: '45deg'}],
              marginLeft: 2,
            }}
          />
        ) : null}
      </View>
    </>
  );

  if (!onPress) {
    return (
      <View testID={testID} style={[styles.row, style]}>
        {content}
      </View>
    );
  }

  return (
    <Pressable
      testID={testID}
      accessibilityRole="button"
      accessibilityLabel={title}
      onPress={onPress}
      style={state => [
        styles.row,
        (state.pressed || (state as {hovered?: boolean}).hovered) && {
          backgroundColor: p.accent,
        },
        style,
      ]}>
      {content}
    </Pressable>
  );
}

/**
 * Группа строк, разбитая по страницам.
 *
 * Списки настроек рисовались целиком: профили терминалов, ключи, сессии, подписчики.
 * Пока их три, это верно, а на тридцати список выносит за нижний край страницы всё,
 * что стоит под ним, — и кнопку «добавить», и объяснение, зачем этот раздел нужен.
 *
 * Рейка появляется, только когда строк БОЛЬШЕ страницы: под тремя ключами она
 * сообщала бы «1–3 из 3», то есть ничего.
 */
export function PagedRows<T>({
  items,
  render,
  keyOf,
  perPage = PAGED_ROWS_PER_PAGE,
  perPageOptions = PAGED_ROWS_SIZES,
  style,
  testID,
}: {
  items: readonly T[];
  render: (item: T) => React.ReactNode;
  keyOf: (item: T) => string;
  perPage?: number;
  perPageOptions?: readonly number[];
  style?: StyleProp<ViewStyle>;
  testID?: string;
}) {
  const [size, setSize] = React.useState<number>(perPage);
  const paged = useClientPage(items, size);

  return (
    <View style={styles.paged}>
      <RowGroup style={style} testID={testID}>
        {paged.rows.map(item => (
          <React.Fragment key={keyOf(item)}>{render(item)}</React.Fragment>
        ))}
      </RowGroup>
      {items.length > perPage ? (
        <Pager
          page={paged.page}
          totalPages={paged.totalPages}
          total={paged.total}
          perPage={paged.perPage}
          onPage={paged.setPage}
          perPageOptions={perPageOptions}
          onPerPage={setSize}
          testID={testID ? `${testID}-pager` : undefined}
        />
      ) : null}
    </View>
  );
}
